import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Button, Alert, Spinner } from 'react-bootstrap';
import { showToast as toast } from '../toastManager';
import { processAssetsAndGenerateTemplates, createFAQTemplate } from '../utils/dynamicTemplateEngine';
import axios from 'axios';
import '../App.css';
import '../DynamicTemplates.css';

const AssetsSection = ({ ViewedPage, shop_domain, app_url }) => {
    ViewedPage(35); // Page ID for Media Section
    
    const [assets, setAssets] = useState([]);
    const [loading, setLoading] = useState(false);
    const [generatedSections, setGeneratedSections] = useState([]);
    const autoSlideIntervalsRef = useRef([]);
    
    console.log('Media Section - shop_domain from prop:', shop_domain);
    console.log('Media Section - app_url:', app_url);

    // Load assets from Block Repository
    const loadAssets = async () => {
        try {
            setLoading(true);
            console.log('Loading assets for Media Section, shop_domain:', shop_domain);
            console.log('API URL:', `${app_url}/api/assets/get_all`);
            
            const response = await axios.post(`${app_url}/api/assets/get_all`, {
                shop_domain: shop_domain
            });
            
            console.log('Assets API response:', response.data);
            
            if (response.data.success) {
                const loadedAssets = response.data.data || [];
                setAssets(loadedAssets);
                console.log('Assets loaded for Media Section:', loadedAssets.length, 'items');
                console.log('First asset:', loadedAssets[0]);
            } else {
                console.error('Failed to load assets:', response.data.message);
                toast.error(response.data.message || 'Failed to load assets');
            }
        } catch (error) {
            console.error('Error loading assets:', error);
            console.error('Error details:', error.response?.data);
            toast.error('Error loading assets. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // Generate default template-based sections using dynamic template engine
    const generateDefaultSections = () => {
        console.log('Generating default sections with assets:', assets);
        console.log('Asset types found:', assets.map(a => a.type));
        
        if (assets.length === 0) {
            console.log('No assets available, showing sample templates');
            setGeneratedSections([]);
            return;
        }

        // Create sample AI response for consistent preview
        const sampleAIResponse = {
            headline: "Featured Content",
            description: "Discover our amazing collection of assets",
            sections: [
                {
                    heading: "Premium Quality",
                    description: "Experience the finest selection of content"
                },
                {
                    heading: "Professional Design", 
                    description: "Crafted with attention to detail"
                },
                {
                    heading: "Easy to Use",
                    description: "Simple and intuitive interface"
                }
            ]
        };

        // Use the same dynamic template engine as AI Asset Content Generator
        const result = processAssetsAndGenerateTemplates(assets, "AssetsSection", "Show me sample templates", sampleAIResponse, app_url);
        
        console.log('Dynamic template engine result:', result);
        
        // Convert templates to sections format
        const sections = result.templates
            .filter(template => template !== null) // Filter out null templates
            .map((template, index) => {
                const previewUrl = result.previewUrls[index];
                return {
                    id: previewUrl?.section_id || `template_${index}`,
                    name: previewUrl?.section_name || `Template ${index + 1}`,
                    description: `Generated template based on your assets`,
                    type: previewUrl?.section_type || 'template',
                    template: template,
                    html: template ? template.outerHTML : ''
                };
            });

        // Check if there are PDF files and add sample FAQ template
        const pdfAssets = assets.filter(asset => asset.type === 'PDF');
        if (pdfAssets.length > 0) {
            const firstPdf = pdfAssets[0];
            const sampleFAQData = {
                headline: `FAQ Sample - ${firstPdf.title || 'Document'}`,
                description: "This is a sample FAQ template. Use the AI Asset Content Generator to create customized FAQ sections from your PDF content.",
                faq: [
                    {
                        question: `What is the main purpose of ${firstPdf.title || 'this document'}?`,
                        answer: `${firstPdf.title || 'This document'} provides comprehensive information and guidelines related to your specific query, offering detailed insights and practical solutions for implementation.`
                    },
                    {
                        question: `How can I effectively use the information in ${firstPdf.title || 'this document'}?`,
                        answer: `Follow the step-by-step instructions provided in ${firstPdf.title || 'this document'}, pay attention to the detailed explanations, and implement the recommendations systematically for optimal results.`
                    },
                    {
                        question: `What are the key benefits mentioned in ${firstPdf.title || 'this document'}?`,
                        answer: `${firstPdf.title || 'This document'} outlines significant advantages including improved efficiency, better understanding of processes, and practical solutions that can be immediately applied.`
                    },
                    {
                        question: `Are there any important considerations when using ${firstPdf.title || 'this document'}?`,
                        answer: `Yes, please review all safety guidelines in ${firstPdf.title || 'this document'}, follow the recommended procedures carefully, and ensure you understand all prerequisites before implementation.`
                    },
                    {
                        question: `Where can I find additional support for ${firstPdf.title || 'this document'}?`,
                        answer: `For further assistance with ${firstPdf.title || 'this document'}, refer to the contact information provided in the document, check the reference materials, or reach out to the support team for clarification.`
                    }
                ]
            };

            const faqTemplate = createFAQTemplate(sampleFAQData, 999);
            if (faqTemplate) {
                sections.push({
                    id: 'faq_sample',
                    name: 'FAQ Sample Template',
                    description: 'Sample FAQ template - Use AI module to generate customized FAQs from your PDF content',
                    type: 'faq',
                    template: faqTemplate,
                    html: faqTemplate.outerHTML,
                    isSample: true
                });
            }
        }

        setGeneratedSections(sections);
    };

    // Load assets on component mount
    useEffect(() => {
        if (shop_domain && app_url) {
            loadAssets();
        }
    }, [shop_domain, app_url]);

    // Auto-generate sections when assets are loaded
    useEffect(() => {
        if (assets.length > 0) {
            generateDefaultSections();
        }
    }, [assets]);

    // Initialize carousel functionality when sections are rendered
    useEffect(() => {
        // Define carousel scroll function globally if not already defined
        if (typeof window.sxi_dynamicScrollCarousel === 'undefined') {
            window.sxi_dynamicScrollCarousel = function(direction, idx) {
                console.log('Scrolling carousel:', direction, idx);
                const carousel = document.getElementById(`sxi_dynamic_carousel_${idx}`);
                if (!carousel) {
                    console.error('Carousel not found:', `sxi_dynamic_carousel_${idx}`);
                    return;
                }
                
                const slide = carousel.querySelector(".sxi_dynamic_carousel_slide");
                if (!slide) {
                    console.error('Slide not found in carousel');
                    return;
                }
                
                const scrollAmount = slide.offsetWidth + 20; // 20px gap
                carousel.scrollBy({
                    left: direction * scrollAmount,
                    behavior: "smooth",
                });
            };
            console.log('Carousel scroll function defined');
        }

        // Initialize FAQ toggle function if not already defined
        if (typeof window.toggleFAQ === 'undefined') {
            window.toggleFAQ = function(templateIdx, faqIdx) {
                const answer = document.getElementById('answer_' + templateIdx + '_' + faqIdx);
                const toggle = document.getElementById('toggle_' + templateIdx + '_' + faqIdx);
                
                if (!answer || !toggle) return;
                
                const template = document.getElementById('dynamic_template_faq_' + templateIdx);
                if (template) {
                    const allAnswers = template.querySelectorAll('.sxi_dynamic_faq_answer');
                    const allToggles = template.querySelectorAll('.sxi_dynamic_faq_toggle');
                    
                    allAnswers.forEach((ans, index) => {
                        if (index !== faqIdx) {
                            ans.style.display = 'none';
                            if (allToggles[index]) {
                                allToggles[index].textContent = '+';
                            }
                        }
                    });
                }
                
                const isOpen = answer.style.display === 'block';
                answer.style.display = isOpen ? 'none' : 'block';
                toggle.textContent = isOpen ? '+' : '−';
            };
        }

        if (generatedSections.length > 0) {
            // Initialize after a short delay to ensure DOM is ready
            const timer = setTimeout(() => {
                console.log('Initializing carousel buttons, sections:', generatedSections.length);
                
                // Add click event listeners using event delegation
                const handleCarouselClick = (e) => {
                    const button = e.target.closest('.sxi_dynamic_carousel_btn');
                    if (!button) return;
                    
                    e.preventDefault();
                    e.stopPropagation();
                    
                    const carouselWrapper = button.closest('.sxi_dynamic_carousel_wrapper');
                    if (carouselWrapper) {
                        const carouselContainer = carouselWrapper.querySelector('.sxi_dynamic_carousel_container');
                        if (carouselContainer) {
                            const carouselId = carouselContainer.id;
                            const direction = button.classList.contains('sxi_dynamic_left') ? -1 : 1;
                            console.log('Carousel button clicked:', carouselId, direction);
                            window.sxi_dynamicScrollCarousel(carouselId, direction);
                        }
                    }
                };
                
                document.addEventListener('click', handleCarouselClick);
                console.log('Carousel event listener added');
                
                // Initialize FAQ click handlers
                const handleFAQClick = (e) => {
                    const question = e.target.closest('.sxi_dynamic_faq_question');
                    if (!question) return;
                    
                    e.preventDefault();
                    e.stopPropagation();
                    
                    const toggle = question.querySelector('.sxi_dynamic_faq_toggle');
                    if (toggle && toggle.id && window.toggleFAQ) {
                        const match = toggle.id.match(/toggle_(\d+)_(\d+)/);
                        if (match) {
                            window.toggleFAQ(parseInt(match[1]), parseInt(match[2]));
                        }
                    }
                };
                
                document.addEventListener('click', handleFAQClick);
                console.log('FAQ event listener added');
                
                // Auto-slide functionality (slides every 5 seconds)
                const carousels = document.querySelectorAll('.sxi_dynamic_carousel_container');
                carousels.forEach((carousel) => {
                    const carouselId = carousel.id;
                    if (carouselId) {
                        const idxMatch = carouselId.match(/sxi_dynamic_carousel_(\d+)/);
                        const idx = idxMatch ? parseInt(idxMatch[1]) : 0;
                        
                        // Auto-slide every 5 seconds
                        const autoSlideInterval = setInterval(() => {
                            if (window.sxi_dynamicScrollCarousel) {
                                window.sxi_dynamicScrollCarousel(1, idx);
                            }
                        }, 5000);
                        
                        autoSlideIntervalsRef.current.push(autoSlideInterval);
                        console.log('Auto-slide started for carousel:', carouselId);
                    }
                });
            }, 200);

            return () => {
                clearTimeout(timer);
                // Cleanup auto-slide intervals
                console.log('Cleaning up auto-slide intervals');
                autoSlideIntervalsRef.current.forEach(interval => clearInterval(interval));
                autoSlideIntervalsRef.current = [];
            };
        }
    }, [generatedSections]);

    // Filter assets based on type
    const filteredAssets = assets.filter(asset => {
        return ['Image', 'Video', 'Video Link', 'PDF', 'Contact'].includes(asset.type);
    });

    return (
        <div className="main-container" style={{padding: '20px 0'}}>
            <Container fluid>
                <Row>
                    <Col md={12}>
                        <div className="page-header" style={{marginBottom: '30px'}}>
                            <div className="main-title">
                            <h2>Media Section</h2>
                            </div>
                            
                            <p style={{color: '#666', fontSize: '16px'}}>Preview how your assets will look in AI-generated templates</p>
                        </div>
                    </Col>
                </Row>

                {loading ? (
                    <Row>
                        <Col md={12} className="text-center">
                            <Spinner animation="border" />
                            <p>Loading assets...</p>
                        </Col>
                    </Row>
                ) : filteredAssets.length === 0 ? (
                    <Row>
                        <Col md={12}>
                            <Alert variant="info" className="text-center">
                                <h4>No Assets Found</h4>
                                <p>You need to add assets to the Block Repository first to create sections.</p>
                                <Button 
                                    variant="primary" 
                                    onClick={() => window.location.href = '/settings?tab=block-repository'}
                                >
                                    Go to Block Repository
                                </Button>
                            </Alert>
                        </Col>
                    </Row>
                ) : (
                    <>
                        <Row>
                            <Col xs={12}>
                                <div className="template-sections-container">
                                    {/* <h5 className="mb-4">Template Sections Preview</h5>
                                    <p className="text-muted mb-4">See how AI-generated templates look with your assets</p>
                                     */}
                                    {generatedSections.length > 0 ? (
                                        <div className="template-sections-preview">
                                            {generatedSections.map(section => (
                                                <div key={section.id} className="template-preview-item">
                                                    <div title='Click to edit this section' ref={(el) => {
                                                        if (el && section.template) {
                                                            // Clear previous content
                                                            el.innerHTML = '';
                                                            // Append the template element
                                                            el.appendChild(section.template);
                                                            
                                                            // Reattach event listeners for carousel buttons after DOM insertion
                                                            setTimeout(() => {
                                                                const buttons = el.querySelectorAll('.sxi_dynamic_carousel_btn');
                                                                console.log('Found carousel buttons:', buttons.length);
                                                                buttons.forEach(button => {
                                                                    const originalOnclick = button.getAttribute('onclick');
                                                                    if (originalOnclick && originalOnclick.includes('sxi_dynamicScrollCarousel')) {
                                                                        // Remove the onclick attribute since React won't execute it
                                                                        button.removeAttribute('onclick');
                                                                        
                                                                        // Parse the onclick to get carousel ID and direction
                                                                        const matches = originalOnclick.match(/sxi_dynamicScrollCarousel\('([^']+)',\s*(-?[0-9]+)\)/);
                                                                        if (matches && matches.length === 3) {
                                                                            const carouselId = matches[1];
                                                                            const direction = parseInt(matches[2]);
                                                                            
                                                                            // Extract index from carousel ID (e.g., 'sxi_dynamic_carousel_10' -> 10)
                                                                            const idxMatch = carouselId.match(/sxi_dynamic_carousel_(\d+)/);
                                                                            const idx = idxMatch ? parseInt(idxMatch[1]) : 0;
                                                                            
                                                                            console.log('Setting up carousel button - ID:', carouselId, 'Direction:', direction, 'Index:', idx);
                                                                            
                                                                            // Add proper event listener with correct parameter order
                                                                            button.addEventListener('click', function(e) {
                                                                                e.preventDefault();
                                                                                e.stopPropagation();
                                                                                console.log('Carousel button clicked - ID:', carouselId, 'Direction:', direction, 'Index:', idx);
                                                                                
                                                                                if (window.sxi_dynamicScrollCarousel) {
                                                                                    // Call with correct parameter order: (direction, idx)
                                                                                    window.sxi_dynamicScrollCarousel(direction, idx);
                                                                                } else {
                                                                                    console.error('sxi_dynamicScrollCarousel function not defined');
                                                                                }
                                                                            });
                                                                        }
                                                                    }
                                                                });
                                                            }, 50);
                                                        }
                                                    }} />
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="text-center text-muted py-5">
                                            <h5>No template sections available</h5>
                                            <p>Add assets to the Block Repository to see template sections</p>
                                            <Button 
                                                variant="primary" 
                                                onClick={() => window.location.href = '/block-repository'}
                                            >
                                                Go to Block Repository
                                            </Button>
                                        </div>
                                    )}
                                </div>
                            </Col>
                        </Row>
                    </>
                )}

            </Container>
        </div>
    );
};

export default AssetsSection;